import { toasterService } from '@ncino/web-components/services'
import toastActionBus from '~/services/toastActionBus'

// useful when a long stack trace / system message gets surfaced accidentally
// prevents toasts from running off the screen and being impossible to close (see INV-2943)
function truncate (str, truncateLength = 600, substrLength = 600) {
  if (str.length > truncateLength) {
    return str.substring(0, substrLength) + '...'
  }
  return str
}

// ngc-toaster's own default is 5000ms; eVault's success/info toasts have always
// been 7s, so pass it explicitly rather than inherit the Gator default.
const AUTO_DISMISS_MS = 7000

// Restores sn-toaster's cap: newest five visible, older ones held back, each
// entry owning its own timer whether showing or not. See CLO-13909.
const MAX_VISIBLE_TOASTS = 5
const toasts = []

function discard (entry) {
  const index = toasts.indexOf(entry)
  if (index === -1) {
    return
  }
  toasts.splice(index, 1)
  if (entry.timer) {
    clearTimeout(entry.timer)
    entry.timer = null
  }
  hide(entry)
  reconcile()
}

function hide (entry) {
  if (entry.id) {
    toasterService.removeToast(entry.id)
    entry.id = null
  }
}

// ngc-toast's `dismiss` event is composed, so a hand-closed toast reaches the
// document. Nothing else tells us the user clicked the X.
if (typeof document !== 'undefined') {
  document.addEventListener('dismiss', (event) => {
    const id = event.detail && event.detail.id
    const entry = id && toasts.find(toast => toast.id === id)
    if (entry) {
      discard(entry)
    }
  })
}

// Permutes ngc-toast-container's private `_renderedToasts` (Gator 16.1.0) so a
// resurfacing toast lands above newer ones. Fails soft to append-order.
function orderStack (container) {
  const rendered = container._renderedToasts
  if (!Array.isArray(rendered) || rendered.length === 0) {
    return
  }

  const visible = toasts.slice(Math.max(0, toasts.length - MAX_VISIBLE_TOASTS))
  const wanted = new Map(visible.map((entry, index) => [entry.id, index]))
  const rank = item => (wanted.has(item.id) ? wanted.get(item.id) : Number.MAX_SAFE_INTEGER)
  const next = rendered.filter(item => !item.initiateRemoval).sort((a, b) => rank(a) - rank(b))

  // Only assign on a real change. Moving a node re-inserts it, which restarts
  // its slide-in animation, so a no-op write would flash the stack.
  const changed = next.length !== rendered.length || next.some((item, index) => item !== rendered[index])
  if (changed) {
    container._renderedToasts = next
  }
}

// Cached, not re-queried: querySelector cannot find the toaster once a torn-down
// modal has detached it, which is exactly when it needs recovering.
let toasterEl = null

function toasterElement () {
  const found = typeof document === 'undefined' ? null : document.querySelector('ngc-toaster')
  if (found) {
    toasterEl = found
  }
  return toasterEl
}

// CLO-13970 ported to ngc-toaster: showModal() makes everything outside the
// dialog inert, so the toaster is moved into an open modal to stay clickable.
let toasterHome = null
let modalObserver = null

function openModal () {
  const modals = Array.from(document.querySelectorAll('ngc-modal'))
  // Last in document order wins, so a modal stacked over another gets it.
  return modals.reverse().find(modal => modal.open || modal.hasAttribute('open')) || null
}

function parkToaster () {
  const toaster = toasterElement()
  if (!toaster) {
    return
  }
  // Never record a modal as home - it would park the toaster somewhere that
  // gets torn down. First call always runs with the toaster at its template spot.
  if (!toasterHome && toaster.parentNode && toaster.parentNode.localName !== 'ngc-modal') {
    toasterHome = toaster.parentNode
  }
  // Home whenever the stack is empty: a toaster left in a closed modal stops
  // being rendered, and findToaster() resolves by visibility.
  const target = (toasts.length > 0 && openModal()) || toasterHome
  if (target && toaster.parentNode !== target) {
    target.appendChild(toaster)
  }
  syncPopover(toaster)
}

// Re-asserted every reconcile: moving the toaster desyncs ngc-toaster's own
// popover bookkeeping, and an unpromoted [popover] silently hides every toast.
function syncPopover (toaster) {
  const shell = toaster.shadowRoot && toaster.shadowRoot.querySelector('.gator-toaster-popover')
  if (!shell) {
    return
  }
  const parked = toaster.parentNode && toaster.parentNode.localName === 'ngc-modal'
  if (parked) {
    // Promotion is lost on the move, and an unpromoted [popover] is display:none.
    // Inside the dialog nothing needs promoting - it is already top layer.
    shell.removeAttribute('popover')
    return
  }
  if (!shell.hasAttribute('popover')) {
    shell.setAttribute('popover', 'manual')
  }
  if (toasts.length > 0 && typeof shell.showPopover === 'function' && !shell.matches(':popover-open')) {
    try {
      shell.showPopover()
    } catch (_err) {
      // Already promoted, or the Popover API is unavailable.
    }
  }
}

function watchModals () {
  if (modalObserver || typeof MutationObserver !== 'function') {
    return
  }
  modalObserver = new MutationObserver(parkToaster)
  modalObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open']
  })
}

function unwatchModals () {
  if (modalObserver) {
    modalObserver.disconnect()
    modalObserver = null
  }
}

function applyStackOrder () {
  const toaster = toasterElement()
  if (!toaster || !toaster.updateComplete) {
    return
  }
  // Lit renders asynchronously, so the container's list is a frame stale until
  // both elements flush. Reordering it early shuffles nodes and restarts animations.
  toaster.updateComplete
    .then(() => {
      const container = toaster.shadowRoot && toaster.shadowRoot.querySelector('ngc-toast-container')
      if (!container || !container.updateComplete) {
        return null
      }
      return container.updateComplete.then(() => orderStack(container))
    })
    .catch(() => {
      // Stack order is a nicety; never surface it as an unhandled rejection.
    })
}

function reconcile () {
  const visible = toasts.slice(Math.max(0, toasts.length - MAX_VISIBLE_TOASTS))
  toasts.forEach((entry) => {
    if (!visible.includes(entry)) {
      hide(entry)
    }
  })
  // Park before presenting: createToast resolves by visibility, so this also
  // makes every raise self-healing if the toaster was stranded.
  if (toasts.length > 0) {
    watchModals()
  } else {
    unwatchModals()
  }
  parkToaster()
  visible.forEach((entry) => {
    if (!entry.id) {
      entry.id = present(entry)
    }
  })
  applyStackOrder()
}

// message/description map to title/subtitle; only sn-toast's sticky types had an
// X. Always duration 0 - our own timer drives dismissal, so hiding never resets it.
function present (entry) {
  const { message, description, variant, sticky, actionText, actionData } = entry.options
  // sn-toast closed itself right after emitting actionClicked, so the action
  // button has to dismiss as well as act; ngc-toast only calls onClick.
  const actions = actionText
    ? [{
      label: actionText,
      onClick: () => {
        toastActionBus.emit(actionData)
        discard(entry)
      }
    }]
    : undefined

  return toasterService.createToast({
    title: message,
    subtitle: truncate(description),
    variant,
    dismissible: sticky,
    duration: 0,
    actions
  })
}

function raise ({ message = '', description = '', variant, sticky = false, actionText = '', actionData = {} }) {
  // The pack mounts even if the Gator chunk fails, leaving ngc-toaster
  // un-upgraded; createToast would then throw out of a Vuex mutation.
  if (typeof customElements === 'undefined' || !customElements.get('ngc-toaster')) {
    return null
  }

  const entry = {
    options: { message, description, variant, sticky, actionText, actionData },
    id: null,
    timer: null
  }
  toasts.push(entry)
  if (!sticky) {
    entry.timer = setTimeout(() => discard(entry), AUTO_DISMISS_MS)
  }
  reconcile()
  return entry
}

export default {
  // Raisers return a handle for later dismissal - Gator's toast id is not stable
  // across hide/re-show. See reports_v2/reportDetails.vue.
  dismiss (handle) {
    if (handle) {
      discard(handle)
    }
  },
  setToast ({ message = '', description = '', event = 'info', actionText = '', actionData = {} }) {
    // Never sticky: sn-toast fell back to type 'simple' here, which auto-closed
    // after 7s whatever the event was. No caller outside the store.
    return raise({ message, description, variant: event, actionText, actionData })
  },
  setSuccessToast ({ message = 'Success', description = '' }) {
    return raise({ message, description, variant: 'success' })
  },
  setSuccessActionToast ({ message = 'Success', description = '', actionText = '', actionData = {} }) {
    return raise({ message, description, variant: 'success', actionText, actionData })
  },
  setSuccessStickyActionToast ({ message = 'Success', description = '', actionText = '', actionData = {} }) {
    return raise({ message, description, variant: 'success', sticky: true, actionText, actionData })
  },
  setInfoToast ({ message = 'Information', description = '' }) {
    return raise({ message, description, variant: 'info' })
  },
  setErrorToast ({ message = 'Error', description = '' }) {
    return raise({ message, description, variant: 'error', sticky: true })
  },
  setWarningToast ({ message = 'Warning', description = '' }) {
    return raise({ message, description, variant: 'warning', sticky: true })
  },
  setErrorStickyActionToast ({ message = 'Error', description = '', actionText = '', actionData = {} }) {
    return raise({ message, description, variant: 'error', sticky: true, actionText, actionData })
  },
  setErrorToastType ({ message = 'Error', description = '' }) {
    // Drops `message` on both branches as snToastService did; honouring it would
    // change visible titles across ~300 call sites. Preserved deliberately.
    if (description.length > 250) {
      // 250 and 125 are arbitrary but look good when the error text is an xml response
      return this.setErrorStickyActionToast({
        description: truncate(description, 250, 125),
        actionText: 'View details',
        actionData: {
          dataSource: 'errorOverflow',
          title: message,
          data: description
        }
      })
    }
    return this.setErrorToast({ description })
  }
}
